import type { Wallet, Approval } from "@/types";
import {
  displayTotal,
  parseUsdValue,
  summarizeWalletValuations,
  UNKNOWN_VALUE_LABEL,
  usdCompact,
  usdPrecise,
  usdStandard,
  walletValuationValue,
  type ValuationStatus,
  type ValuationSummary,
} from "@/features/valuation/valuation";

export { usdCompact, usdStandard };

export function formatAsOf(): string {
  return `${new Date().toLocaleTimeString("en-US", {
    timeZone: "UTC",
    hour: "2-digit",
    minute: "2-digit",
  })} UTC`;
}

export function getTotalAum(wallets: Wallet[]): ValuationSummary {
  return summarizeWalletValuations(wallets);
}

/** Headline label when there is no total to show at all. */
export const AUM_UNAVAILABLE_LABEL = "Valuation unavailable";

/** Sub-line when the total covers every wallet in the portfolio. */
export const AUM_ALL_VALUED_LABEL = "Across valued wallets";

/** Why a total with unvalued wallets is only part of the picture (J4 / ALL-5638). */
export function aumExcludedLabel(unvaluedWalletCount: number): string {
  return `Excludes ${unvaluedWalletCount} unvalued wallet${unvaluedWalletCount === 1 ? "" : "s"}`;
}

/**
 * The one sentence for "this total leaves wallets out" — `22 wallets not
 * valued`. The hero KPI and the AUM composition both take it from here and
 * read `ValuationSummary.unvaluedWalletCount`, so the two AUM figures on the
 * page always state the same exclusion; a second phrasing in a second place
 * is how the numbers drifted apart.
 */
export function aumUnvaluedWalletLabel(unvaluedWalletCount: number): string {
  return `${unvaluedWalletCount} wallet${unvaluedWalletCount === 1 ? "" : "s"} not valued`;
}

/** The hero KPI's sub-line under the Total AUM figure. */
export function getAumKpiSub(summary: ValuationSummary): string {
  const { unavailable, unvaluedWalletCount } = getAumTotalDisplay(summary);
  if (unavailable) return AUM_UNAVAILABLE_LABEL;
  return unvaluedWalletCount > 0 ? aumUnvaluedWalletLabel(unvaluedWalletCount) : AUM_ALL_VALUED_LABEL;
}

function aumUnavailableReason(summary: ValuationSummary): string {
  return summary.unvaluedWalletCount > 0
    ? `No current valuation for ${summary.unvaluedWalletCount} wallet${summary.unvaluedWalletCount === 1 ? "" : "s"}`
    : "No valuation total was returned for these wallets";
}

export interface AumTotalDisplay {
  /** Headline total, always two decimals. Never a dollar figure when nothing is valued. */
  value: string;
  /**
   * `Excludes N unvalued wallets` when the total covers only some wallets,
   * `Valuation unavailable` when there is no total at all, and `null` when
   * every wallet is valued.
   */
  label: string | null;
  /** Why the figure is unavailable; `null` whenever `value` is a real total. */
  reason: string | null;
  unavailable: boolean;
  unvaluedWalletCount: number;
  status: ValuationStatus;
}

/**
 * The Total AUM figure as the home dashboard shows it: the exact sum of the
 * valued wallets to two decimals, labelled when wallets were excluded, and an
 * explicit `Valuation unavailable` (with a reason) rather than `$0.00` when
 * nothing has been valued — an unknown total and a genuine zero balance are
 * different facts and must not render the same way.
 */
export function getAumTotalDisplay(summary: ValuationSummary): AumTotalDisplay {
  const { value } = displayTotal(summary);
  // A blank total string is an absent total, not a zero one: `Number("")` is 0,
  // so parsing it directly would render a real-looking `$0.00`.
  const total = typeof value === "string" && value.trim() === "" ? null : parseUsdValue(value);

  if (total === null) {
    return {
      value: UNKNOWN_VALUE_LABEL,
      label: AUM_UNAVAILABLE_LABEL,
      reason: aumUnavailableReason(summary),
      unavailable: true,
      unvaluedWalletCount: summary.unvaluedWalletCount,
      status: summary.status,
    };
  }

  return {
    value: usdPrecise.format(total),
    label: summary.unvaluedWalletCount > 0 ? aumExcludedLabel(summary.unvaluedWalletCount) : null,
    reason: null,
    unavailable: false,
    unvaluedWalletCount: summary.unvaluedWalletCount,
    status: summary.status,
  };
}

export function getWalletChainCounts(wallets: Wallet[]) {
  const chainCount = new Set(wallets.map((w) => w.chain)).size;
  return { walletCount: wallets.length, chainCount };
}

export function getActiveWalletCount(wallets: Wallet[]): number {
  return wallets.filter((w) => w.status === "active").length;
}

export interface WalletTypeItem {
  type: string;
  value: number;
  pct: number;
}

/**
 * Splits `targetCents` across `weights` in proportion to their size, largest
 * remainder first, so the parts add up to the target exactly. Rounding each
 * part on its own (a plain `weights[i] * scale`) leaves the rows a cent or two
 * short of the total they are meant to decompose, which reads as a figure that
 * went missing.
 */
function apportionCents(weights: number[], targetCents: number): number[] {
  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
  if (weightTotal <= 0) return weights;
  const exact = weights.map((weight) => (weight / weightTotal) * targetCents);
  const cents = exact.map((value) => Math.floor(value));
  let remaining = targetCents - cents.reduce((sum, value) => sum + value, 0);
  const byRemainder = exact
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const { index } of byRemainder) {
    if (remaining <= 0) break;
    cents[index] += 1;
    remaining -= 1;
  }
  return cents;
}

/**
 * Per-wallet-type AUM, as the composition's type rows show it.
 *
 * `total` is the *same* `ValuationSummary` the hero renders, and passing it is
 * what keeps the two figures on the dashboard equal. The rows alone are summed
 * off the wallet list, which is a different measurement of the same portfolio:
 * a wallet with no verified quote carries the gateway's reference price
 * (`applyWalletValuation` in hooks/use-wallets.ts), so on dev the rows added up
 * to $4.1K under a $736.1 hero total — raw balances against valued USD. With
 * `total` given, the rows are the portfolio's verified total apportioned by the
 * wallet-type mix, so they always decompose the headline figure and never
 * exceed it. Without it (an empty or wholly unvalued portfolio, or a caller
 * that has no summary) the local mix is returned untouched.
 */
export function getAumByWalletType(wallets: Wallet[], total?: ValuationSummary): WalletTypeItem[] {
  const portfolio = wallets.reduce((sum, w) => sum + (walletValuationValue(w) ?? 0), 0);
  const map = new Map<string, number>();
  for (const w of wallets) {
    const value = walletValuationValue(w);
    if (value === null) continue;
    if (value <= 0) continue;
    const label = w.type[0].toUpperCase() + w.type.slice(1);
    map.set(label, (map.get(label) ?? 0) + value);
  }
  const items = Array.from(map.entries())
    .map(([type, value]) => ({ type, value, pct: portfolio > 0 ? value / portfolio : 0 }))
    .sort((a, b) => b.value - a.value);

  if (total === undefined || items.length === 0) return items;

  const headline = parseUsdValue(displayTotal(total).value);
  const headlineCents = headline === null ? 0 : Math.round(headline * 100);
  const mixCents = items.map((item) => Math.round(item.value * 100));
  // No headline to decompose (Valuation unavailable), or a portfolio the
  // gateway did not value at all: the rows keep their own measured values
  // rather than being scaled onto a figure that is not a total.
  if (headlineCents <= 0) return items;
  if (mixCents.reduce((sum, cents) => sum + cents, 0) === headlineCents) return items;

  const shares = apportionCents(mixCents, headlineCents);
  return items.map((item, index) => ({ ...item, value: shares[index] / 100 }));
}

export interface ChainDistributionItem {
  chain: string;
  value: number;
  pct: number;
}

export function getChainDistribution(wallets: Wallet[]): ChainDistributionItem[] {
  const total = wallets.reduce((sum, w) => sum + (walletValuationValue(w) ?? 0), 0);
  const map = new Map<string, number>();
  for (const w of wallets) {
    const value = walletValuationValue(w);
    if (value === null || value <= 0) continue;
    map.set(w.chain, (map.get(w.chain) ?? 0) + value);
  }
  const items = Array.from(map.entries())
    .map(([chain, value]) => ({
      chain,
      value,
      pct: total > 0 ? value / total : 0,
    }))
    .sort((a, b) => b.value - a.value);
  return items;
}

export function getChainDistributionGrouped(wallets: Wallet[], othersThreshold = 0.05): ChainDistributionItem[] {
  const items = getChainDistribution(wallets);
  const total = items.reduce((sum, i) => sum + i.value, 0);
  const major = items.filter((i) => i.pct >= othersThreshold);
  const minor = items.filter((i) => i.pct < othersThreshold);
  if (minor.length > 0) {
    const othersValue = minor.reduce((sum, i) => sum + i.value, 0);
    major.push({
      chain: "Others",
      value: othersValue,
      pct: total > 0 ? othersValue / total : 0,
    });
  }
  return major.sort((a, b) => b.value - a.value);
}

export function getTopChainByAum(wallets: Wallet[]): string | null {
  return getChainDistribution(wallets)[0]?.chain ?? null;
}

export function getTopWallets(wallets: Wallet[], limit = 3): Wallet[] {
  return wallets
    .filter((w) => {
      const value = walletValuationValue(w);
      return value !== null && value > 0;
    })
    .sort((a, b) => (walletValuationValue(b) ?? 0) - (walletValuationValue(a) ?? 0))
    .slice(0, limit);
}

/**
 * The home dashboard shows the caller's own pending-approval queue (the
 * `mine` scope from `useApprovals`, already filtered server-side), sorted so
 * urgent items surface first. Real `Approval` records mapped from the
 * gateway (`mapApiApproval` in `hooks/use-approvals.ts`) never carry
 * SLA/urgency fields the backend doesn't expose (ALL-4734 G6): `urgent` is
 * always `false` and `slaSeverity` is always `"ok"` today, so this sort is a
 * no-op until the backend adds that data — which is honest, not a bug.
 */
export function getPendingApprovals(approvals: Approval[]): Approval[] {
  return approvals
    .filter((a) => a.status === "pending")
    .sort((a, b) => {
      if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;
      const order = { danger: 0, warn: 1, ok: 2 } as const;
      return order[a.slaSeverity] - order[b.slaSeverity];
    });
}

export interface AttentionItem {
  tone: "warning" | "danger" | "info";
  text: string;
  href: string;
}

/**
 * Attention items are built only from data this page actually has: the
 * caller's pending-approval count and unread-notification count. There is
 * no backend for provider/chain health, monitoring alerts, or
 * reconciliation exceptions (G1 "out of scope" — no `/v1/monitoring/*` or
 * `/v1/chain/*` gateway route exists), so those are never fabricated here.
 * When a source errored, its count is omitted rather than shown as 0, and
 * the caller (`AttentionBar`) is told via `hasError` not to claim
 * "all systems operational" when the truth is "unknown".
 */
export function getAttentionItems(
  pendingApprovalsCount: number | null,
  unreadNotificationsCount: number | null,
): AttentionItem[] {
  const items: AttentionItem[] = [];
  if (pendingApprovalsCount !== null && pendingApprovalsCount > 0) {
    items.push({
      tone: "warning",
      text: `${pendingApprovalsCount} pending approval${pendingApprovalsCount > 1 ? "s" : ""}`,
      href: "/approvals",
    });
  }
  if (unreadNotificationsCount !== null && unreadNotificationsCount > 0) {
    items.push({
      tone: "info",
      text: `${unreadNotificationsCount} unread notification${unreadNotificationsCount > 1 ? "s" : ""}`,
      href: "/home#notifications",
    });
  }
  return items;
}
